import React from 'react';

/**
 * ApniCRM Design System — premium, cohesive, mobile-first.
 * Deep navy + gold accents for a jewelry-store feel.
 */

const panelBase =
  'bg-white border border-slate-200/70 rounded-2xl shadow-[0_1px_3px_rgba(15,23,42,0.06),0_8px_24px_rgba(15,23,42,0.04)]';

export const GlassPanel: React.FC<{
  children: React.ReactNode;
  className?: string;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
}> = ({ children, className = '', onClick }) => {
  return (
    <div className={`${panelBase} ${className}`} onClick={onClick}>
      {children}
    </div>
  );
};

export const GlassButton: React.FC<
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    variant?: 'primary' | 'secondary' | 'danger' | 'gold';
  }
> = ({ children, variant = 'primary', className = '', ...props }) => {
  const variants = {
    primary:
      'bg-slate-900 text-white hover:bg-slate-800 active:bg-slate-950 shadow-sm shadow-slate-900/20 border border-slate-900',
    secondary:
      'bg-white text-slate-800 border border-slate-200 hover:border-slate-300 hover:bg-slate-50 active:bg-slate-100',
    danger:
      'bg-red-600 text-white hover:bg-red-700 active:bg-red-800 shadow-sm shadow-red-600/20 border border-red-600',
    gold: 'bg-gradient-to-r from-amber-500 to-amber-600 text-white hover:from-amber-600 hover:to-amber-700 active:from-amber-700 active:to-amber-800 shadow-sm shadow-amber-500/30 border border-amber-500',
  };

  return (
    <button
      type="button"
      className={`inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 ${variants[variant]} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
};

export const GlassInput: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = (props) => {
  return (
    <input
      {...props}
      className={`w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100 transition-all ${props.className || ''}`}
    />
  );
};

export const GlassSelect: React.FC<React.SelectHTMLAttributes<HTMLSelectElement>> = (props) => {
  return (
    <select
      {...props}
      className={`w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-900 focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100 transition-all ${props.className || ''}`}
    />
  );
};

export const GlassCard: React.FC<{
  children: React.ReactNode;
  className?: string;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
}> = ({ children, className = '', onClick }) => {
  return (
    <div
      className={`bg-white border border-slate-200/70 rounded-2xl p-4 shadow-[0_1px_3px_rgba(15,23,42,0.06),0_6px_16px_rgba(15,23,42,0.04)] transition-shadow duration-200 hover:shadow-[0_2px_6px_rgba(15,23,42,0.08),0_12px_28px_rgba(15,23,42,0.07)] ${className}`}
      onClick={onClick}
    >
      {children}
    </div>
  );
};

/** Page header used consistently across every page */
export const PageHeader: React.FC<{
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  icon?: React.ReactNode;
}> = ({ title, subtitle, action, icon }) => {
  return (
    <div className="mb-6 flex flex-col sm:flex-row sm:items-center gap-4 animate-fade-in-up">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-3">
          {icon && (
            <span className="hidden sm:inline-flex w-11 h-11 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 text-white items-center justify-center shadow-sm shadow-amber-500/30">
              {icon}
            </span>
          )}
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">{title}</h1>
        </div>
        {subtitle && <p className="text-slate-500 mt-1 text-sm sm:text-base">{subtitle}</p>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
};

/** Consistent empty state */
export const EmptyState: React.FC<{
  icon?: React.ReactNode;
  title: string;
  message?: string;
  action?: React.ReactNode;
}> = ({ icon, title, message, action }) => {
  return (
    <div className="text-center py-14 px-4 animate-fade-in">
      {icon && (
        <div className="mx-auto mb-4 w-16 h-16 rounded-2xl bg-slate-100 text-slate-400 flex items-center justify-center text-2xl">
          {icon}
        </div>
      )}
      <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
      {message && <p className="text-slate-500 text-sm mt-1 max-w-sm mx-auto">{message}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
};

/** Loading spinner */
export const LoadingSpinner: React.FC<{ label?: string; className?: string }> = ({
  label = 'Loading...',
  className = '',
}) => {
  return (
    <div className={`flex flex-col items-center justify-center py-16 ${className}`}>
      <div className="animate-spin w-9 h-9 border-[3px] border-slate-200 border-t-amber-500 rounded-full" />
      <p className="text-slate-500 text-sm mt-4">{label}</p>
    </div>
  );
};